/**
 * Helper utilities for dealing with layout/flex issues
 */
import {globalEmit} from "@/composables/globalEventBus";
import globalLogger from "@/logging";
import type {Ref} from "vue";
import {onMounted, onUnmounted, ref} from "vue";
import debounce from "lodash/debounce";
import {any} from "ramda";

const logger = globalLogger.getLogger("layoutUtils");

const TOP_BUFFER = 10; // this should be small enough to not affect real non-wrapping scenarios

/**
 * when any of the children are "below" the top of the parent, then there is wrapping
 * @param parent
 */
export const childrenAreWrapped = (parent: Element) => {
  const parentPadding = parseInt(window.getComputedStyle(parent, null).getPropertyValue("padding-top"));
  return any((child: Element) => {
    // @ts-ignore - offsetTop exists but not in the Type
    return child.offsetTop > parent.offsetTop + parentPadding + TOP_BUFFER;
  }, Array.from(parent.children));
};

/**
 * Apply a given class to the parent and children if wrapping is detected
 * @param parent
 * @param cls
 */
export const applyClassWhenWrapped = (parent: Element, cls: string): boolean => {
  if (childrenAreWrapped(parent)) {
    parent.classList.add(cls);
    Array.from(parent.children).forEach((child: Element) => child.classList.add(cls));
    return true;
  } else {
    parent.classList.remove(cls);
    Array.from(parent.children).forEach((child: Element) => child.classList.remove(cls));
    return false;
  }
};

/**
 * Set up the resizeObserver
 *
 * @param wrapperElement - element to observe
 * @param classQuery - class query to find element
 * @param wrappedClass - class to apply when wrapping
 * @param emit - function to emit "wrapped" event
 */
export const monitorElementForWrapping = (
  wrapperElement: Ref<Element>, // this has to be passed in or Vue can't find the element
  classQuery: string,
  wrappedClass: string,
  emit: Function = () => {},
) => {
  const resizeObserver: Ref<ResizeObserver> = ref(null);

  const resizeHandler = debounce((entries: ResizeObserverEntry[]) => {
    const wrapped = any((entry) => {
      return applyClassWhenWrapped(entry.target, wrappedClass);
    }, entries);

    emit("wrapped", wrapped);
    globalEmit("layout:wrapped", {elementQuery: classQuery, wrapped: wrapped});
  }, 200);

  onMounted(() => {
    setTimeout(() => {
      resizeObserver.value = new ResizeObserver(resizeHandler);
      wrapperElement.value = document.querySelector(classQuery);
      if (wrapperElement.value) {
        logger.trace(() => `setting resizeObserver on ${classQuery}`);
        resizeObserver.value.observe(wrapperElement.value);
      } else {
        logger.warn(() => `wrapperElement not found ${classQuery}`);
      }
    }, 500);
  });

  onUnmounted(() => {
    if (wrapperElement.value) {
      resizeObserver.value.unobserve(wrapperElement.value);
      resizeObserver.value.disconnect();
    }
  });
};
